import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks";
import { fetchProbationReviewsMineThunk } from "./probation-reviews.slice";
import ProbationReviewDialog from "./ProbationReviewDialog";
import { formatDay } from "features/personnel/personnel-helpers";
import { ProbationReviewReason } from "services/probation-review.service";
import useIsPhone from "features/files/use-is-phone";

const REASON_KEY: Record<ProbationReviewReason, string> = {
  manager: "probation_review_reason_manager",
  no_manager: "probation_review_reason_no_manager",
  escalated: "probation_review_reason_escalated",
};

/**
 * Tasks → «Bandomojo laikotarpio vertinimai» (docs/prd/probation-salary-review.md
 * §7.5): every review the current user (line manager, or HR when escalated /
 * no manager) may decide or is watching. Its data lives in the
 * probation-reviews slice.
 */
export default function ProbationReviewsQueue() {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isPhone = useIsPhone();
  const { companyId: sliceCompanyId, items: sliceItems, loaded, isLoading, isError } = useAppSelector(
    (state) => state.probationReviewsReducer
  );
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const items = sliceCompanyId === companyId && loaded ? sliceItems : [];

  const reload = useCallback(() => {
    if (companyId) dispatch(fetchProbationReviewsMineThunk(companyId));
  }, [companyId, dispatch]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!companyId) return null;

  return (
    <div className="space-y-4 p-4">
      <p className="text-sm text-gray-500">{t("probation_reviews_intro")}</p>

      {isLoading && items.length === 0 ? (
        <p className="text-sm text-gray-500">{t("loading")}</p>
      ) : isError && items.length === 0 ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{t("probation_reviews_load_failed")}</span>
          <button
            type="button"
            onClick={reload}
            className="rounded-sm border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            {t("retry")}
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
          {t("probation_reviews_empty")}
        </div>
      ) : isPhone ? (
        <ul className="space-y-3">
          {items.map((row) => (
            <li key={row.userId}>
              <button
                type="button"
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-left text-sm"
                onClick={() => setSelectedUserId(row.userId)}
              >
                <span className="block font-medium text-blue-700">{row.employeeName || "—"}</span>
                <span className="block text-gray-600">{row.positionLT || "—"}</span>
                <span className="mt-2 block text-gray-700">
                  {t("probation_decision_deadline")}: {formatDay(row.decisionDeadline)}
                </span>
                <span className="block text-gray-700">
                  {t("probation_end_date_field")}: {formatDay(row.probationEndDate)}
                </span>
                <span className="mt-1 block text-xs text-gray-500">{t(REASON_KEY[row.reason])}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-4">{t("employee")}</th>
                <th className="py-2 pr-4">{t("position")}</th>
                <th className="py-2 pr-4">{t("probation_end_date_field")}</th>
                <th className="py-2 pr-4">{t("probation_decision_deadline")}</th>
                <th className="py-2 pr-4">{t("probation_review_reason")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.userId}
                  className="cursor-pointer border-b border-gray-100 align-top hover:bg-gray-50"
                  onClick={() => setSelectedUserId(row.userId)}
                >
                  <td className="py-2 pr-4">
                    <button
                      type="button"
                      className="text-left font-medium text-blue-700 hover:underline"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedUserId(row.userId);
                      }}
                    >
                      {row.employeeName || "—"}
                    </button>
                  </td>
                  <td className="py-2 pr-4">{row.positionLT || "—"}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">{formatDay(row.probationEndDate)}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">{formatDay(row.decisionDeadline)}</td>
                  <td className="py-2 pr-4">{t(REASON_KEY[row.reason])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedUserId && companyId && (
        <ProbationReviewDialog
          companyId={companyId}
          userId={selectedUserId}
          open
          onClose={() => setSelectedUserId(null)}
          onSaved={reload}
        />
      )}
    </div>
  );
}
